import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { get } from 'lodash-es';
import { connect } from 'react-redux';
/* Models */
import { InventoryEntities } from 'client/data/models/inventory';
/* Components */
import { Link } from 'site-modules/shared/components/link/link';
/* Utils */
import { getStaticImageUrl } from 'client/utils/image-helpers';
import { isCapOneProvider } from 'site-modules/shared/utils/financing/financing';
import { formatPriceStringV2 } from 'site-modules/shared/utils/price-utils';
import { getCAFPrequalUrl } from 'site-modules/shared/utils/financing/caf-utils';
/* Constants */
import { FINANCING_CAF_CREATIVE_ID } from 'site-modules/shared/constants/financing/financing-constants';

import './capital-one-entry-point-main-well.scss';

export function CapitalOneEntryPointMainWellUI({
  vinData = {},
  isMobile = false,
  payments = {},
  isCapOneIneligible = false,
  ted6504Recipe = 'ctrl',
  isProdEnv = false,
}) {
  const CAPITAL_ONE_CAR_IN_HAND_SRC = getStaticImageUrl('/img/car-buying/capital1/cap_one_red_car.png');
  const URL = `/financing/${isCapOneIneligible ? 'prequalify-and-shop' : vinData.vin}/basics`;
  const SUBTITLE =
    "And, once you're pre-qualified, you'll be able to see your real monthly rate and payment on eligible cars.";
  const PAYMENT_DETAILS = [
    { name: 'Payment', value: `${formatPriceStringV2(get(payments, 'loan.monthlyPayment', 0))} / mo`, showIcon: true },
    { name: 'APR', value: `${get(payments, 'loan.apr', 0)}%`, additionalClasses: 'px-1 apr-field' },
    { name: 'Months', value: `${get(payments, 'loan.numberOfMonths', 60)}` },
  ];
  const isCapOne = isCapOneProvider(vinData);
  const isTED6504Enabled = ['chal-1', 'chal-2'].includes(ted6504Recipe);

  const renderPaymentDetail = (name, value) => (
    <>
      <div className="size-12 fw-bold">{value}</div>
      <div className="size-9">{name}</div>
    </>
  );

  return (
    <div
      className="capital-one-main-well-container text-start bg-cool-gray-90 mb-1_5 pos-r pt-2 pt-md-1_5 pe-lg-1_5"
      data-tracking-parent={
        isTED6504Enabled
          ? FINANCING_CAF_CREATIVE_ID
          : `used-summary_financing-${isCapOne ? 'matched' : 'unmatched'}-vin${isCapOneIneligible ? '-ineligible' : ''}`
      }
    >
      <div className="d-flex flex-column flex-lg-row">
        <div className="me-lg-2 d-flex">
          <img
            width={351}
            height={196}
            src={CAPITAL_ONE_CAR_IN_HAND_SRC}
            alt=""
            loading="lazy"
            className={classNames({ 'align-self-end': !isMobile })}
          />
        </div>
        <div className="ps-1 ps-lg-0_25 pe-1 pe-lg-0">
          <h2
            className="fw-bold text-cool-gray-40 size-24 my-1 my-lg-0"
            style={{
              lineHeight: '30px',
            }}
          >
            Pre-qualify with <b>no impact</b> to your credit score
          </h2>
          <div className="payment-table first bg-white pos-a" />
          <ul
            className="payment-table second bg-white pos-a p-0_5 d-flex justify-content-between align-items-center list-unstyled"
            aria-label="Capital one payment information"
          >
            {PAYMENT_DETAILS.map(({ name, value, showIcon, additionalClasses }) => (
              <li
                className={classNames(showIcon ? 'd-flex align-items-center' : 'text-center', additionalClasses)}
                key={name}
              >
                {showIcon ? (
                  <>
                    <i className="icon-checkmark text-blue-50 me-0_5" aria-hidden />
                    <div className="text-center">{renderPaymentDetail(name, value)}</div>
                  </>
                ) : (
                  renderPaymentDetail(name, value)
                )}
              </li>
            ))}
          </ul>

          <div className="text-cool-gray-30 mt-md-1 mb-1">{SUBTITLE}</div>
          <Link
            to={isTED6504Enabled ? getCAFPrequalUrl(vinData, isProdEnv, ted6504Recipe) : URL}
            className={classNames('btn btn-sm btn-success fw-bold size-16 mb-1_5 text-transform-none')}
            data-tracking-id="financing_cta"
            data-tracking-value="Get Pre-qualified"
            {...(isTED6504Enabled && { target: '_blank' })}
          >
            <span>Get Pre-qualified</span>
          </Link>
        </div>
      </div>
    </div>
  );
}

CapitalOneEntryPointMainWellUI.propTypes = {
  vinData: InventoryEntities.InventoryVin,
  isMobile: PropTypes.bool,
  payments: PropTypes.shape({
    loan: PropTypes.shape({
      monthlyPayment: PropTypes.number,
      apr: PropTypes.number,
      numberOfMonths: PropTypes.number,
    }),
  }),
  isCapOneIneligible: PropTypes.bool,
  ted6504Recipe: PropTypes.string,
  isProdEnv: PropTypes.bool,
};

export const mapStateToProps = state => ({
  isProdEnv: get(state, 'venomEnv') === 'prod',
});

export const CapitalOneEntryPointMainWell = connect(mapStateToProps)(CapitalOneEntryPointMainWellUI);
